import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import api from '../services/api';
import dayjs from 'dayjs';
import { stableStringify } from '../utils/stableStringify';

const CallContext = createContext();

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};

function callsRequestKey(requestPayload, dateRange, searchTerm) {
  const dateKey = dateRange
    ? stableStringify({
        ar: dateRange.all_results,
        cs: dateRange.currentStart?.format?.('YYYY-MM-DD'),
        ce: dateRange.currentEnd?.format?.('YYYY-MM-DD'),
        fid: dateRange.filter?.id,
      })
    : 'all';
  return `${stableStringify(requestPayload)}_${dateKey}_${searchTerm || ''}`;
}

function statusCountsRequestKey(dateRangeState, appliedFilters) {
  const currentDateRange = dateRangeState
    ? {
        start_date: dateRangeState.currentStart?.format?.('YYYY-MM-DD'),
        end_date: dateRangeState.currentEnd?.format?.('YYYY-MM-DD'),
        all_results: dateRangeState.all_results || dateRangeState.filter?.id === 'All',
      }
    : null;
  return `${stableStringify(currentDateRange)}_${stableStringify(appliedFilters || {})}`;
}

export const CallProvider = ({ children }) => {
  const [calls, setCalls] = useState(null);
  const [callData, setCallData] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState({});
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [defaultFilterOptions, setDefaultFilterOptions] = useState({});
  const [statusCounts, setStatusCounts] = useState({});

  const lastCallKeyRef = useRef(null);
  const lastSuccessfulCallsRef = useRef(null);
  const fetchCallsInflightRef = useRef(new Map());

  const lastStatusCountsKeyRef = useRef(null);
  const statusCountsSnapshotRef = useRef({});
  const statusCountsInflightRef = useRef(new Map());

  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    statusCountsSnapshotRef.current = statusCounts;
  }, [statusCounts]);

  const cleanup = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    lastCallKeyRef.current = null;
    lastSuccessfulCallsRef.current = null;
    fetchCallsInflightRef.current.clear();
    lastStatusCountsKeyRef.current = null;
    statusCountsInflightRef.current.clear();
  }, []);

  const [loading, setLoading] = useState({
    calls: false,
    callData: false,
  });

  const [errors, setErrors] = useState({
    calls: null,
    callData: null,
  });

  const fetchCalls = useCallback(
    async (requestPayload, dateRange = null, searchTerm = null, forceRefresh = false) => {
      const requestKey = callsRequestKey(requestPayload, dateRange, searchTerm);

      if (!forceRefresh && lastCallKeyRef.current === requestKey) {
        return lastSuccessfulCallsRef.current;
      }

      if (forceRefresh) {
        fetchCallsInflightRef.current.delete(requestKey);
      } else {
        const inflight = fetchCallsInflightRef.current.get(requestKey);
        if (inflight) return inflight;
      }

      const promise = (async () => {
        setLoading((prev) => ({ ...prev, calls: true }));
        try {
          const response = await api.post('/lms/calls/', requestPayload);
          const callsData = response.data || response;
          setCalls(callsData);
          setFilteredCalls(callsData);
          lastCallKeyRef.current = requestKey;
          lastSuccessfulCallsRef.current = callsData;
          return callsData;
        } catch (error) {
          console.error('Error fetching calls:', error);
          return lastSuccessfulCallsRef.current;
        } finally {
          setLoading((prev) => ({ ...prev, calls: false }));
          fetchCallsInflightRef.current.delete(requestKey);
        }
      })();

      fetchCallsInflightRef.current.set(requestKey, promise);
      return promise;
    },
    []
  );

  const fetchCallData = useCallback(async (callId) => {
    if (!callId) return;

    try {
      setLoading((prev) => ({ ...prev, callData: true }));
      setErrors((prev) => ({ ...prev, callData: null }));

      const response = await api.get(`/lms/calls/${callId}/`);
      setCallData(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching call details:', error);
      setErrors((prev) => ({
        ...prev,
        callData: error.response?.data?.detail || 'Failed to fetch call details',
      }));
      throw error;
    } finally {
      setLoading((prev) => ({ ...prev, callData: false }));
    }
  }, []);

  const fetchDefaultFilterOptions = useCallback(async (dateRange = null) => {
    try {
      let startDate;
      let endDate;

      if (dateRange) {
        if (dateRange.currentStart && dateRange.currentEnd) {
          startDate = dateRange.currentStart;
          endDate = dateRange.currentEnd;
        } else if (dateRange.startDate && dateRange.endDate) {
          startDate = dateRange.startDate;
          endDate = dateRange.endDate;
        } else {
          endDate = dayjs();
          startDate = endDate.subtract(30, 'day');
        }
      } else {
        endDate = dayjs();
        startDate = endDate.subtract(30, 'day');
      }

      if (dateRange && !dateRange.all_results && startDate && endDate) {
        const start_date = startDate.format('YYYY-MM-DD');
        const end_date = endDate.format('YYYY-MM-DD');
        const response = await api.get(
          `/lms/calls/filters/default/?start_date=${start_date}&end_date=${end_date}`
        );
        setDefaultFilterOptions(response.data);
      } else {
        setDefaultFilterOptions({});
      }
    } catch (error) {
      console.error('Error fetching default filter options:', error);
    }
  }, []);

  const fetchStatusCounts = useCallback(async (dateRangeState = null, appliedFilters = null) => {
    const requestKey = statusCountsRequestKey(dateRangeState, appliedFilters);

    if (requestKey === lastStatusCountsKeyRef.current) {
      return statusCountsSnapshotRef.current;
    }

    const inflight = statusCountsInflightRef.current.get(requestKey);
    if (inflight) return inflight;

    const promise = (async () => {
      try {
        let startDate;
        let endDate;

        if (dateRangeState && dateRangeState.currentStart && dateRangeState.currentEnd) {
          startDate = dateRangeState.currentStart;
          endDate = dateRangeState.currentEnd;
        } else {
          startDate = dayjs();
          endDate = dayjs();
        }

        const requestPayload = {
          all_results: dateRangeState
            ? dateRangeState.all_results || dateRangeState.filter?.id === 'All'
            : true,
        };

        if (!requestPayload.all_results) {
          requestPayload.date_range = {
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
          };
        }

        if (appliedFilters && appliedFilters.search) {
          requestPayload.search = appliedFilters.search;
        }

        const response = await api.post('/lms/calls/status-counts/', requestPayload);

        if (response && response.data) {
          lastStatusCountsKeyRef.current = requestKey;
          return response.data;
        }
        lastStatusCountsKeyRef.current = requestKey;
        return {};
      } catch (error) {
        console.error('Error fetching status counts:', error);
        if (error.response) {
          console.error('Error response data:', error.response.data);
        } else if (error.request) {
          console.error('Error request:', error.request);
        } else {
          console.error('Error message:', error.message);
        }
        return {};
      } finally {
        statusCountsInflightRef.current.delete(requestKey);
      }
    })();

    statusCountsInflightRef.current.set(requestKey, promise);
    return promise;
  }, []);

  const markCallAsLead = useCallback(async (payload) => {
    try {
      const response = await api.post('/lms/calls/mark-as-lead/', payload);
      return response.data;
    } catch (error) {
      console.error('Error marking call as lead:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const value = useMemo(
    () => ({
      calls,
      callData,
      loading,
      errors,
      filteredCalls,
      appliedFilters,
      selectedStatus,
      defaultFilterOptions,
      statusCounts,
      fetchCalls,
      fetchCallData,
      fetchDefaultFilterOptions,
      fetchStatusCounts,
      setAppliedFilters,
      setSelectedStatus,
      setStatusCounts,
      cleanup,
      markCallAsLead,
    }),
    [
      calls,
      callData,
      loading,
      errors,
      filteredCalls,
      appliedFilters,
      selectedStatus,
      defaultFilterOptions,
      statusCounts,
      fetchCalls,
      fetchCallData,
      fetchDefaultFilterOptions,
      fetchStatusCounts,
      cleanup,
      markCallAsLead,
    ]
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};

export default CallContext;
